/**
 * Math.sinh polyfill
 * IE, Android 4.2など
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/sinh#Polyfill
 */
Math.sinh = Math.sinh || function(x) {
  const y = Math.exp(x)
  return (y - 1 / y) / 2
}

/**
 * 地図をタイル状に分割する際の
 * タイル座標 <-> 緯度経度の相互変換に関するロジックを扱うクラス
 *
 * @see http://www.mapli.net/blogs/google-maps-mechanism/how-google-map-works5/
 *
 */
/* global google */
export default class Tile {
  constructor(x, y, z) {
    this.x = x
    this.y = y
    this.z = z
  }

  // 地図のズームレベルが変動しても取得するデータは変わらないので、
  // ズームレベル12以上のときはズームレベル12のタイルサイズで描画
  // (APIリクエスト回数を少なくするため)
  static MAX_ZOOM = 12

  // 地図を分割するタイル1辺のピクセルサイズ
  static SIZE = 256

  static R = (Tile.SIZE / 2) / Math.PI

  /**
   * 地図の境界(ビューポート)を覆うのに必要なタイル配列を返す
   *
   * @param bounds google.maps.LatLngBounds
   * @param zoom Number
   * @return Array<Tile>
   */
  static tilesCoveringBounds(bounds, zoom) {
    return Tile.tilesBetween(
      Tile.at(bounds.getSouthWest(), zoom),
      Tile.at(bounds.getNorthEast(), zoom)
    )
  }

  /**
   * 2つのタイルの間を埋めるタイル配列を返す
   *
   * @param tile Tile
   * @param other Tile
   * @return Array<Tile>
   */
  static tilesBetween(tile, other) {
    const minX = Math.min(tile.x, other.x)
    const maxX = Math.max(tile.x, other.x)
    const minY = Math.min(tile.y, other.y)
    const maxY = Math.max(tile.y, other.y)

    const tiles = []
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        tiles.push(Tile.findOrCreateBy(x, y, tile.z))
      }
    }
    return tiles
  }

  /**
   * 指定した地点(緯度・経度)を含むタイルを返す
   *
   * @param latLng google.maps.LatLng
   * @param zoom Number
   * @return Tile
   */
  static at(latLng, zoom) {
    const coord = Tile.latLngToCoords(latLng)

    // ズームレベルに応じたタイルサイズの段階を2回に1回にする
    //   => ズームレベルを2段階変更したら1回タイル更新が起こる
    // かつ、ズームレベルを-1する
    //   => 本来のタイルサイズより1段階大きいタイルにする
    // NOTE: APIリクエスト数削減のため
    const z = (Math.floor(zoom / 2) * 2) - 1

    // 基準座標をzoomレベルまで引き伸ばし、
    // Tile.SIZEで割った商がタイル左上の座標
    const x = Math.floor(coord.x * Math.pow(2, z) / Tile.SIZE)
    const y = Math.floor(coord.y * Math.pow(2, z) / Tile.SIZE)

    return Tile.findOrCreateBy(x, y, z)
  }

  /**
   * 緯度・経度をタイル分割システムのXY座標に変換
   *
   * @param latLng google.maps.LatLng
   * @return google.maps.Point
   */
  static latLngToCoords(latLng) {
    const latRadiant = latLng.lat() * Math.PI / 180
    const lngRadiant = latLng.lng() * Math.PI / 180
    return new google.maps.Point(
      Tile.R * (lngRadiant + Math.PI),
      -Tile.R / 2 * Math.log( (1 + Math.sin(latRadiant)) / (1 - Math.sin(latRadiant)) ) + (Tile.SIZE / 2)
    )
  }

  /**
   * タイル分割システムのXY座標をzoomレベルにおける緯度・経度に変換
   *
   * @param x Number
   * @param y Number
   * @param z Number
   * @return google.maps.LatLng
   */
  static coordsToLatLng(x, y, z) {
    const worldCoord = new google.maps.Point(
      (x * Tile.SIZE) / Math.pow(2, z),
      (y * Tile.SIZE) / Math.pow(2, z)
    )
    const latRadiant = Math.atan(Math.sinh( ((Tile.SIZE / 2) - worldCoord.y) / Tile.R))
    const lngRadiant = worldCoord.x / Tile.R - Math.PI
    return new google.maps.LatLng(
      latRadiant * 180 / Math.PI,
      lngRadiant * 180 / Math.PI
    )
  }

  static instances = {}
  static findOrCreateBy(x, y, z) {
    const key = `${x}:${y}:${z}`
    return Tile.instances[key] || (Tile.instances[key] = new Tile(x, y, z))
  }

  /**
   * タイルの識別子
   *
   * @return String
   */
  get id() {
    return `${this.x}:${this.y}:${this.z}`
  }

  /**
   * タイル左上の緯度・経度
   *
   * @return google.maps.LatLng
   */
  get latLng() {
    return Tile.coordsToLatLng(this.x, this.y, this.z)
  }

  /**
   * タイル南西点(左下)の緯度・経度
   *
   * @return google.maps.LatLng
   */
  get southWest() {
    return Tile.coordsToLatLng(this.x, this.y + 1, this.z)
  }

  /**
   * タイル北東点(右上)の緯度・経度
   *
   * @return google.maps.LatLng
   */
  get northEast() {
    return Tile.coordsToLatLng(this.x + 1, this.y, this.z)
  }

  /**
   * タイルの緯度・経度境界
   *
   * @return google.maps.LatLngBounds
   */
  get bounds() {
    return new google.maps.LatLngBounds(this.southWest, this.northEast)
  }

  /**
   * タイルの緯度・経度境界の文字列フォーマット(APIへのクエリパラメータ形式)
   * "south,west|north,east"
   *
   * @return String
   */
  toString() {
    const { southWest, northEast } = this
    return `${southWest.lat()},${southWest.lng()}|${northEast.lat()},${northEast.lng()}`
  }
}
